/*
 * Заглушка формы контента: плоская ink-плитка плюс бегущий белый блик.
 * Кадры @keyframes shimmer и общий блок prefers-reduced-motion живут
 * в app/styles/base.css, здесь их нет.
 *
 * Как собран блик. Заливка — в background-color, блик — отдельным background-image
 * шириной 220% с прозрачными краями и no-repeat:
 *   — края градиента прозрачные, а не ink: иначе в перекрытом участке альфа удвоилась бы
 *     и по плитке пошёл шов;
 *   — no-repeat даёт ровно один проход блика за цикл, без скачка на повторе;
 *   — стартовая позиция 220% 0 совпадает с кадром from в base.css. При
 *     prefers-reduced-motion анимация обнуляется и позиция возвращается к объявленной:
 *     картинка блика стоит за пределами плитки, остаётся ровная видимая заглушка.
 * Значения 220% / 34% / 50% / 66% — геометрия эффекта, связанная с кадрами; в токены
 * вынесены только цвета.
 *
 * Ступени высоты берутся из уже существующих шкал: строка — кегли текста, капсула —
 * высоты контролов (Q4), круг — диаметры аватара. Скелетон обязан занимать ровно столько
 * места, сколько займёт контент, иначе подстановка данных двигает раскладку.
 */

.root {
  --skeleton-height: var(--font-size-lg);
  --skeleton-radius: var(--radius-xs);

  display: block;
  flex: 0 0 auto;
  width: 100%;
  height: var(--skeleton-height);
  background-color: var(--color-skeleton);
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    transparent 34%,
    var(--color-skeleton-highlight) 50%,
    transparent 66%,
    transparent 100%
  );
  background-repeat: no-repeat;
  background-position: 220% 0;
  background-size: 220% 100%;
  border-radius: var(--skeleton-radius);
  animation: var(--anim-shimmer);
}

/* Формы */

/* Строка текста: артикул, название, бренд, цена, дата */
.line {
  --skeleton-radius: var(--radius-xs);
}

/* Капсула: бейдж статуса, чип фильтра, кнопка, степпер количества */
.pill {
  --skeleton-height: var(--control-h-md);
  --skeleton-radius: var(--radius-pill);
}

/* Круг: аватар контрагента, логотип бренда, круглая кнопка-иконка */
.circle {
  --skeleton-height: var(--avatar-size-md);
  --skeleton-radius: var(--radius-circle);

  width: var(--skeleton-height);
}

/* Плитка изображения: фото товара, превью документа. Высоту задаёт пропорция */
.photo {
  --skeleton-radius: var(--radius-field);

  height: auto;
  aspect-ratio: 4 / 3;
}

/* Отведённая область: график дашборда, карта складов, превью PDF.
   Высоту даёт родитель; min-height не даёт заглушке схлопнуться в нить. */
.block {
  --skeleton-radius: var(--radius-field);

  flex: 1 1 auto;
  height: 100%;
  min-height: var(--control-h-lg);
}

/* Ступени высоты. Смысл ступени зависит от формы, поэтому селекторы составные. */

.line.sizeXs {
  --skeleton-height: var(--font-size-xs);
}

.line.sizeSm {
  --skeleton-height: var(--font-size-sm);
}

.line.sizeMd {
  --skeleton-height: var(--font-size-lg);
}

.line.sizeLg {
  --skeleton-height: var(--font-size-3xl);
}

.pill.sizeXs {
  --skeleton-height: var(--control-h-xs);
}

.pill.sizeSm {
  --skeleton-height: var(--control-h-sm);
}

.pill.sizeMd {
  --skeleton-height: var(--control-h-md);
}

.pill.sizeLg {
  --skeleton-height: var(--control-h-lg);
}

.circle.sizeXs {
  --skeleton-height: var(--avatar-size-xs);
}

.circle.sizeSm {
  --skeleton-height: var(--avatar-size-sm);
}

.circle.sizeMd {
  --skeleton-height: var(--avatar-size-md);
}

.circle.sizeLg {
  --skeleton-height: var(--avatar-size-lg);
}

/* Доля ширины родителя. 13 процентов кита (44…100) сведены к четырём ступеням (Q9):
   это пропорция раскладки, а не дизайн-значение, поэтому токенов под неё нет. */

.widthFull {
  width: 100%;
}

.widthLong {
  width: 88%;
}

.widthMedium {
  width: 66%;
}

.widthShort {
  width: 46%;
}

/* Остаток flex-строки: заглушка кнопки рядом с круглой кнопкой-иконкой */
.grow {
  flex: 1 1 auto;
  width: auto;
}

/*
 * В Windows high contrast фон и градиенты вычищаются системой — плитка исчезла бы
 * целиком. Рамка системным цветом оставляет видимую заглушку, анимация выключается:
 * двигать больше нечего.
 */
@media (forced-colors: active) {
  .root {
    border: var(--border-hairline) solid GrayText;
    animation: none;
  }
}

/* Подпись слышна скринридеру и не занимает места в потоке */
.label {
  composes: visuallyHidden from '@/shared/styles/visuallyHidden.module.css';
}
